import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  addPropertyTypeService,
  getPropertyTypeService,
  getPropertyTypeByIDService,
  updatePropertyTypeService,
  deletePropertyTypeService,
} from '../../services/property-type.service';

const initialState = {
  propertyTypeList: {
    totalCount: 0,
    totalPage: 0,
    propertyTypesData: [],
  },
  propertyTypeData: null,
};

export const getPropertyType = createAsyncThunk(
  `PROPERTY_TYPE`,
  async ({ page, limit, sortField, sortType, search }, { rejectWithValue }) => {
    try {
      const response = await getPropertyTypeService(
        page,
        limit,
        sortField,
        sortType,
        search
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const addPropertyType = createAsyncThunk(
  'ADD_PROPERTY_TYPE',
  async (body, { rejectWithValue }) => {
    try {
      const response = await addPropertyTypeService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getPropertyTypeById = createAsyncThunk(
  'GET_PROPERTY_TYPE',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getPropertyTypeByIDService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const updatePropertyType = createAsyncThunk(
  `UPDATE_PROPERTY_TYPE`,
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await updatePropertyTypeService(id, body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const deletePropertyType = createAsyncThunk(
  'DELETE_PROPERTY_TYPE',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deletePropertyTypeService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const propertyTypeSlice = createSlice({
  name: 'propertyType',
  initialState,
  reducers: {
    resetPropertyType: (state) => {
      state.propertyTypeData = null;
    },
    resetPropertyTypeList: (state) => {
      state.propertyTypeList = {
        totalCount: 0,
        totalPage: 0,
        propertyTypesData: [],
      };
    },
  },
  extraReducers: (builder) => {
    // Get Property Type
    builder
      .addCase(getPropertyType.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const {
            count = 0,
            totalPage = 0,
            propertyTypes = [],
          } = payload?.data?.data;
          state.propertyTypeList = {
            totalCount: count,
            totalPage,
            propertyTypesData: [...propertyTypes],
          };
        }
      })
      .addCase(getPropertyType.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Add Property Type
    builder
      .addCase(addPropertyType.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(addPropertyType.rejected, (state, { payload }) => {
        throw payload;
      });

    // Get Property Type Details
    builder
      .addCase(getPropertyTypeById.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.propertyTypeData = payload?.data?.data;
        }
      })
      .addCase(getPropertyTypeById.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Update Property Type
    builder
      .addCase(updatePropertyType.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(updatePropertyType.rejected, (_, { payload }) => {
        throw payload;
      });

    // Delete Property Type
    builder
      .addCase(deletePropertyType.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(deletePropertyType.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });
  },
});

export const { resetPropertyType, resetPropertyTypeList } =
  propertyTypeSlice.actions;

export default propertyTypeSlice.reducer;
